import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getUserOrders } from '../../services/order.service';
import UserProfile from './UserProfile';
import ProjectList from './ProjectLis';
import SavedProjectList from './SavedProjectList';
import { SidebarLayout } from '../../components/Sidebar';
import { useAuthStore } from '../../store/authStore';
import Update from './Update';
import SearchComponent from '../../components/clients/SearchComponent';
import ReviewModal from '../../components/reviews/ReviewModal';
import { createReview } from '../../services/review.service';
import { useAlert } from '../../context/AlertContext';

const Projects = () => {
  const { user } = useAuthStore();
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [activeNavItem, setActiveNavItem] = useState('/dashboard');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Review Modal State
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const setAlert = useAlert();
  const queryClient = useQueryClient();

  const { data: ongoingOrdersData, isLoading: ongoingLoading } = useQuery({
    queryKey: ['orders', 'ongoing'],
    queryFn: () => getUserOrders({ type: 'purchases' })
  });
  
  const { data: historyOrdersData, isLoading: historyLoading } = useQuery({
    queryKey: ['orders', 'history'],
    queryFn: () => getUserOrders({ type: 'purchases', status: 'completed,cancelled,disputed' })
  });

  const handleReviewClick = (project) => {
    setSelectedOrder(project);
    setIsReviewModalOpen(true);
  };

  const handleReviewSubmit = async (reviewData) => {
    if (!selectedOrder) return;
    try {
      await createReview({
        orderId: selectedOrder.id,
        gigId: selectedOrder.gigId,
        rating: reviewData.rating,
        comment: reviewData.comment,
        deliverySpeed: reviewData.deliverySpeed
      });
      setAlert({ type: 'success', message: 'Review submitted successfully!' });
      queryClient.invalidateQueries(['orders', 'ongoing']); // Invalidate ongoing orders to reflect review status if applicable
      queryClient.invalidateQueries(['orders', 'history']); // Invalidate history orders to reflect review status
      setIsReviewModalOpen(false);
    } catch (error) {
      setAlert({ type: 'error', message: error.response?.data?.error || 'Failed to submit review' });
    }
  };

  // Filter and map to ProjectList props shape
  const ongoingProjects = (ongoingOrdersData?.orders || [])
    .filter(order => order.status === 'pending' || order.status === 'in-progress')
    .filter(order => {
      if (!searchQuery) return true;
      const q = searchQuery.toLowerCase();
      return (
        order.title?.toLowerCase().includes(q) ||
        (order.gigCategory?.[0] || 'Project').toLowerCase().includes(q)
      );
    })
    .map(order => ({
      id: order._id,
      category: order.gigCategory?.[0] || 'Project',
      title: order.title,
      price: `$${order.price.toFixed(2)}`,
      image: order.img || order.gigId?.cover,
      status: order.status,
      chatId: order.chatId?._id || order.chatId,
      gigId: order.gigId?._id || order.gigId,
      isReviewed: order.isReviewed,
      additionalPayments: order.additionalPayments
    }));

  const historyProjects = (historyOrdersData?.orders || [])
    .filter(order => {
      if (!searchQuery) return true;
      const q = searchQuery.toLowerCase();
      return (
        order.title?.toLowerCase().includes(q) ||
        (order.gigCategory?.[0] || 'Project').toLowerCase().includes(q)
      );
    })
    .map(order => ({
      id: order._id,
      category: order.gigCategory?.[0] || 'Project',
      title: order.title,
      price: `$${order.price.toFixed(2)}`,
      image: order.img || order.gigId?.cover,
      status: order.status,
      chatId: order.chatId?._id || order.chatId,
      gigId: order.gigId?._id || order.gigId,
      isReviewed: order.isReviewed
    }));

  return (
    <SidebarLayout currentPath="/projects">
      <div className="flex flex-col md:flex-row min-h-screen bg-[#F3FDFECC] dark:bg-[#0b0e14] font-['Poppins']">
        <div className="flex-1">
          <SearchComponent onSearch={setSearchQuery} onChange={setSearchQuery} />
          <div className="md:flex md:gap-6 p-4 md:p-6">
            {/* Left column - projects */}
            <div className="md:flex-1 space-y-6">
              <Update/>
              <div className="md:hidden">
                <UserProfile
                  name={user?.name || 'User'}
                  title={user?.role || 'Client'}
                  hasNotification={true}
                />
              </div>
              <div className="flex flex-col lg:flex-row items-start gap-4">
                <ProjectList 
                  title="Ongoing Projects" 
                  showMoreOptions={false} 
                  projects={ongoingProjects}
                  isLoading={ongoingLoading}
                />
                <SavedProjectList searchQuery={searchQuery} />
              </div>
            </div>
                     
            {/* Right column - profile and history */}
            <div className="mt-6 mb-10 md:mt-0 md:w-80 space-y-6 p-3 rounded-[10px]">
              <div className="hidden md:block">
                <UserProfile
                  name={user?.name || 'User'}
                  title={user?.role || 'Client'}
                  hasNotification={true}
                />
              </div>
              <ProjectList 
                title="Your Project History" 
                showMoreOptions={true} 
                projects={historyProjects}
                isLoading={historyLoading}
                onReviewClick={handleReviewClick}
              />
            </div>
          </div>
                 
       
        </div>
      </div>

      <ReviewModal 
        isOpen={isReviewModalOpen}
        onClose={() => setIsReviewModalOpen(false)}
        onSubmit={handleReviewSubmit}
        orderTitle={selectedOrder?.title}
      />
    </SidebarLayout>
  );
};

export default Projects;
